<template>
  <div class="decimal-input">
    <input
      ref="input"
      v-bind="$attrs"
      :value="displayValue"
      type="text"
      inputmode="decimal"
      autocomplete="off"
      class="decimal-input__field"
      @input="onInput"
      @blur="onBlur"
      @change="$emit('change', $event)"
      @keydown.up.prevent="stepBy(1)"
      @keydown.down.prevent="stepBy(-1)"
    />
    <div class="decimal-input__stepper" aria-hidden="true">
      <button
        type="button"
        tabindex="-1"
        class="decimal-input__step decimal-input__step--up"
        @mousedown.prevent="startStepping(1)"
        @mouseup="stopStepping"
        @mouseleave="stopStepping"
      >
        <fluent-icon icon="chevron-up" size="10" />
      </button>
      <button
        type="button"
        tabindex="-1"
        class="decimal-input__step decimal-input__step--down"
        @mousedown.prevent="startStepping(-1)"
        @mouseup="stopStepping"
        @mouseleave="stopStepping"
      >
        <fluent-icon icon="chevron-down" size="10" />
      </button>
    </div>
  </div>
</template>

<script>
import {
  formatDecimalInput,
  parseDecimalInput,
  sanitizeDecimalInput,
  stepDecimalValue,
} from 'dashboard/helper/numericInputHelper';

const HOLD_DELAY = 400;
const HOLD_INTERVAL = 50;

export default {
  inheritAttrs: false,
  props: {
    value: { type: [Number, String], default: '' },
    allowNegative: { type: Boolean, default: false },
    maxFractionDigits: { type: Number, default: 2 },
  },
  data() {
    return {
      displayValue: formatDecimalInput(this.value, {
        maxFractionDigits: this.maxFractionDigits,
      }),
    };
  },
  computed: {
    options() {
      return {
        allowNegative: this.allowNegative,
        maxFractionDigits: this.maxFractionDigits,
      };
    },
  },
  watch: {
    value(newValue) {
      if (parseDecimalInput(this.displayValue, this.options) === newValue) {
        return;
      }
      this.displayValue = formatDecimalInput(newValue, this.options);
    },
  },
  beforeDestroy() {
    this.stopStepping();
  },
  methods: {
    onInput(event) {
      const sanitized = sanitizeDecimalInput(event.target.value, this.options);
      event.target.value = sanitized;
      this.displayValue = sanitized;
      this.$emit('input', parseDecimalInput(sanitized, this.options));
    },
    onBlur(event) {
      this.displayValue = formatDecimalInput(
        parseDecimalInput(this.displayValue, this.options),
        this.options
      );
      this.$emit('blur', event);
    },
    stepBy(direction) {
      const next = stepDecimalValue(
        parseDecimalInput(this.displayValue, this.options),
        direction,
        this.options
      );
      this.displayValue = formatDecimalInput(next, this.options);
      this.$emit('input', next);
      this.$emit('change');
    },
    startStepping(direction) {
      this.stopStepping();
      this.$refs.input.focus();
      this.stepBy(direction);
      this.holdTimeout = setTimeout(() => {
        this.holdInterval = setInterval(
          () => this.stepBy(direction),
          HOLD_INTERVAL
        );
      }, HOLD_DELAY);
    },
    stopStepping() {
      clearTimeout(this.holdTimeout);
      clearInterval(this.holdInterval);
      this.holdTimeout = null;
      this.holdInterval = null;
    },
  },
};
</script>

<style scoped lang="scss">
.decimal-input {
  margin-bottom: var(--space-normal);
  position: relative;
}

.decimal-input__field {
  margin-bottom: 0;
  padding-right: var(--space-large);
}

.decimal-input__stepper {
  display: flex;
  flex-direction: column;
  position: absolute;
  right: var(--space-micro);
  top: var(--space-micro);
  bottom: var(--space-micro);
  width: var(--space-normal);
}

.decimal-input__step {
  align-items: center;
  background: var(--s-50);
  border: none;
  color: var(--s-600);
  cursor: pointer;
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 0;

  &:hover {
    background: var(--s-100);
    color: var(--s-800);
  }

  &--up {
    border-radius: var(--border-radius-small) var(--border-radius-small) 0 0;
  }

  &--down {
    border-radius: 0 0 var(--border-radius-small) var(--border-radius-small);
  }
}
</style>
